import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import InstagramIcon from '@/assets/icons/social/Instagram.svg';
import LinkedinIcon from '@/assets/icons/social/Linkedin.svg';
import TiktokIcon from '@/assets/icons/social/Tiktok.svg';
import TwitterIcon from '@/assets/icons/social/Twitter.svg';
import YoutubeIcon from '@/assets/icons/social/Youtube.svg';
import { X, Plus } from 'lucide-react';

type SocialKey = 'instagram' | 'linkedin' | 'youtube' | 'twitter' | 'tiktok';

interface SocialItem {
  type: SocialKey;
  link: string;
}

const SOCIAL_OPTIONS: Array<{ type: SocialKey; label: string; icon: string }> = [
  { type: 'instagram', label: 'Instagram', icon: InstagramIcon },
  { type: 'linkedin', label: 'LinkedIn', icon: LinkedinIcon },
  { type: 'youtube', label: 'YouTube', icon: YoutubeIcon },
  { type: 'twitter', label: 'Twitter', icon: TwitterIcon },
  { type: 'tiktok', label: 'TikTok', icon: TiktokIcon }
];

interface SocialListInputProps {
  value?: SocialItem[];
  onChange: (_value: SocialItem[]) => void;
}

export const SocialListInput: React.FC<SocialListInputProps> = ({
  value = [],
  onChange
}) => {
  const [items, setItems] = useState<SocialItem[]>(value);

  useEffect(() => {
    setItems(value);
  }, [value]);

  const availableTypes = useMemo(() => {
    const used = new Set(items.map((item) => item.type));
    return SOCIAL_OPTIONS.filter((option) => !used.has(option.type));
  }, [items]);

  const handleAdd = () => {
    if (availableTypes.length === 0) {
      return;
    }
    const next = [...items, { type: availableTypes[0].type, link: '' }];
    setItems(next);
    onChange(next);
  };

  const handleRemove = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    setItems(next);
    onChange(next);
  };

  const handleTypeChange = (index: number, type: SocialKey) => {
    const next = items.map((item, i) =>
      i === index ? { ...item, type } : item
    );
    setItems(next);
    onChange(next);
  };

  const handleLinkChange = (index: number, link: string) => {
    const next = items.map((item, i) =>
      i === index ? { ...item, link } : item
    );
    setItems(next);
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          onClick={handleAdd}
          disabled={availableTypes.length === 0}
        >
          <Plus className="w-4 h-4" />
          Adicionar rede
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Nenhuma rede adicionada
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => {
            const option = SOCIAL_OPTIONS.find((opt) => opt.type === item.type);
            return (
              <div
                key={`${item.type}-${index}`}
                className="flex flex-col md:flex-row gap-3 md:items-center border border-gray-200 rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  {option && (
                    <div className="w-10 h-10 rounded-full border border-black flex items-center justify-center">
                      <img src={option.icon} alt={option.label} className="w-5 h-5" />
                    </div>
                  )}
                  <div className="min-w-[160px]">
                    <Select
                      value={item.type}
                      onValueChange={(value: SocialKey) => handleTypeChange(index, value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SOCIAL_OPTIONS.map((opt) => (
                          <SelectItem
                            key={opt.type}
                            value={opt.type}
                            disabled={
                              opt.type !== item.type &&
                              items.some(
                                (existing, i) => existing.type === opt.type && i !== index
                              )
                            }
                          >
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex-1">
                  <Input
                    className="w-full"
                    placeholder="URL da rede social (opcional)"
                    value={item.link}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleLinkChange(index, event.target.value)
                    }
                  />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleRemove(index)}
                  className="self-start text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

